<template>
  <component :is="component" :class="componentClasses">

    <!-- buttons leading a-selects -->
    <template v-if="topLevel">
      <template v-if="selectedChildConfig">
        <p v-if="selectedChildConfig.displayName !== 'Hyperlink'">
          {{selectedChildConfig.displayName}}
        </p>
      </template>

      <!-- buttons for links creations -->
      <row v-else class="quickmenu-links">
        <column v-for="(childConfig, index) in builderConfig.children" :key="`${level}-${index}`" class="link">
          <button type="button" class="btn clear-to-blue" @click="configSelected(childConfig)">
            <icon :class="StringHelper.toDash(childConfig.displayName)"></icon>
            {{childConfig.displayName}}
          </button>
        </column>
      </row>
    </template>

    <template v-else-if="hasInstanceObjectSelectChild">
      <icon class="arrow-fat right" color="Blue"></icon>
      <bookmark-link-builder-instance-object-selector
        :builder-config="builderConfig"
        :level="level"
        :is-editing="isEditing"
      />
    </template>

    <template v-else-if="hasInputChild">
        <label for="link-address">
          Link Address
          <help-tooltip v-if="isEditing" :text="textInputHelpText"></help-tooltip>
        </label>
        <input
          v-model="textInput"
          type="text"
          id="link-address"
          class="form-control"
          :class="{'is-invalid': textInputInvalid}"
          :disabled="!isEditing"
        >

      <div class="invalid-feedback">
        does not appear to be a valid URL
      </div>
    </template>

    <template v-else-if="builderConfig.children">
        <icon class="arrow-fat right" color="Blue"></icon>
        <a-select
          :options="childrenSelectOptions"
          bind-by-object
          :value="childrenSelectValue"
          @input="configSelected"
          :disabled="!isEditing"
        />
    </template>
  </component>
</template>

<script lang="ts">
  import Vue from 'vue';
  import * as _ from 'lodash';
  import BookmarkLinkBuilderInstanceObjectSelector from "./bookmark-link-builder-instance-object-selector.vue";
  import Folder from "../../../document_management/models/folder";
  import {IVOptionSetLocal} from "../../../../common/interfaces/vSelectInterfaces";
  import HelpText from "~/components/helpText.vue";
  import {StringHelper} from "../../../../common/helpers/stringHelper";
  import {Hash} from "~/interfaces/hashInterfaces";
  import {ListViewIconSize} from "~/models/listViews/listViewIconSize";

  export default Vue.extend({
    components: {
      BookmarkLinkBuilderInstanceObjectSelector,
      HelpText,
    },
    props: {
      builderConfig: {
        type: Object,
        required: true
      },
      level: {
        type: Number,
        required: true,
      },
      isEditing: {
        type: Boolean,
        required: true,
      },
    },
    data: () => {
      return {
        StringHelper: StringHelper,
        colSize: {
          'hyperlink': 'sz120',
          'dashboards': 'sz120',
          'main-pages': 'sz120',
          'reporting-and-submissions': 'sz240',
          'document-folders': 'sz160',
        }
      }
    },
    computed: {
      selectedChildConfig() {
        return this.$store.state.bookmarkLinkBuilderInterfaceStore.selectedConfigs[this.level + 1];
      },
      topLevel() {
        return (this.level === 0);
      },
      hasInstanceObjectSelectChild() {
        return (this.builderConfig.children && this.builderConfig.children[0].instanceObjectSelect);
      },
      hasInputChild() {
        return (this.builderConfig.children && this.builderConfig.children[0].textInput);
      },
      childrenSelectOptions(): IVOptionSetLocal {
        return {
          options: this.builderConfig.children,
          labelKey: "displayName",
          valueKey: "displayName", // displayName is the only key guaranteed to be there :S
        };
      },
      childrenSelectValue() {
        return this.selectedChildConfig ? this.selectedChildConfig.displayName : null;
      },
      textInput: {
        get(): string {
          return this.$store.state.bookmarkLinkBuilderInterfaceStore.textInput;
        },
        set(value: string) {
          this.$store.commit('bookmarkLinkBuilderInterfaceStore/setTextInput', value);
        }
      },
      textInputInvalid() {
        return this.$store.state.bookmarkLinkBuilderInterfaceStore.textInputInvalid;
      },
      textInputHelpText() {
        return "Either a full URL (e.g. 'https://onestop.md.gov/'), or an absolute path (e.g. '/dashboard')";
      },
      selectedFolder: {
        get(): Folder|null {
          return this.$store.state.bookmarkLinkBuilderInterfaceStore.selectedInstanceObject;
        },
        set(folder: Folder|null): void {
          this.$store.commit('bookmarkLinkBuilderInterfaceStore/setSelectedInstanceObject', folder);
          this.$store.commit('bookmarkLinkBuilderInterfaceStore/setSelectedInstanceObjectType', 'Folder');
        }
      },
      component(): string {
        return this.selectedChildConfig && this.selectedChildConfig.displayName === 'Hyperlink' ? 'div' : 'column';
      },
      componentClasses(): Hash<boolean> {
        const classes: Hash<boolean> = {};
        return {
          'link-dropdown': !this.hasInputChild && (this.builderConfig.children || this.hasInstanceObjectSelectChild)
        }
      },
    },
    methods: {
      configSelected(childConfig) {
        this.$emit('showBackButton');
        this.$store.commit('bookmarkLinkBuilderInterfaceStore/setSelectedConfig', {config: childConfig, level: (this.level + 1)});
      },
    }
  })
</script>
